import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import type { MarketingEvent } from '@bcc/shared';
import { apiFetch } from '../lib/api';
import { apiUrl } from '../lib/apiUrl';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { PhoneField } from '../components/PhoneField';
import { SITE_FORM_CONTROL, SITE_GLASS_CARD } from '../constants/siteUi';

const titlePink = 'text-[var(--header-shell-bg)]';

type RegRow = {
  id: string;
  event_id: string;
  email: string;
  full_name: string;
  phone: string | null;
  ticket_code: string | null;
  attended_at: string | null;
  created_at: string;
  marketing_events: MarketingEvent | null;
};

function IconPin({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={2} className={className} aria-hidden>
      <path d="M12 21s7-4.35 7-10a7 7 0 10-14 0c0 5.65 7 10 7 10z" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="11" r="2" />
    </svg>
  );
}

function EventRsvpForm({
  eventId,
  token,
  onSuccess,
}: {
  eventId: string;
  token: string;
  onSuccess?: () => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [idleSuccess, setIdleSuccess] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    try {
      const res = await fetch(apiUrl(`/api/public/events/${eventId}/rsvp`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ full_name: name.trim(), email: email.trim(), phone: phone.trim() || undefined }),
      });
      if (!res.ok) throw new Error('RSVP failed');
      const data = (await res.json()) as { already?: boolean };
      const msg = data.already ? 'Already registered — confirmation sent.' : 'RSVP received — check your email.';
      setName('');
      setEmail('');
      setPhone('');
      setIdleSuccess(msg);
      setShowForm(false);
      onSuccess?.();
    } catch {
      setFormError('Could not submit. Try again later.');
    }
  }

  if (!showForm) {
    return (
      <div className="mt-4 space-y-2">
        {idleSuccess ? (
          <p className="text-xs font-medium text-emerald-200/95" role="status">
            {idleSuccess}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => {
            setIdleSuccess(null);
            setFormError(null);
            setShowForm(true);
          }}
          className={`w-full rounded-lg border border-white/15 bg-white/[0.06] py-2.5 text-center font-display text-[0.62rem] font-bold uppercase tracking-[0.12em] ${titlePink} hover:bg-white/10`}
        >
          Click to RSVP
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={(evt) => void submit(evt)} className="mt-3 space-y-2">
      <p className="font-display text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]">RSVP</p>
      <input
        required
        placeholder="Full name"
        value={name}
        onChange={(ev) => setName(ev.target.value)}
        className={`w-full ${SITE_FORM_CONTROL}`}
      />
      <input
        required
        type="email"
        placeholder="Email"
        value={email}
        onChange={(ev) => setEmail(ev.target.value)}
        className={`w-full ${SITE_FORM_CONTROL}`}
      />
      <PhoneField
        placeholder="Phone (optional)"
        value={phone}
        onChange={setPhone}
        selectClassName={`${SITE_FORM_CONTROL} min-w-[7rem] shrink-0 cursor-pointer py-2 font-body text-xs`}
        inputClassName={`w-full ${SITE_FORM_CONTROL}`}
      />
      <div className="flex flex-wrap gap-2 pt-1">
        <button
          type="submit"
          className={`rounded-lg px-4 py-2 font-display text-[0.62rem] font-semibold uppercase tracking-[0.12em] ${titlePink} bg-white/10 hover:bg-white/15`}
        >
          Submit RSVP
        </button>
        <button
          type="button"
          onClick={() => {
            setShowForm(false);
            setFormError(null);
          }}
          className="rounded-lg border border-white/12 px-4 py-2 text-xs text-[var(--home-muted-on-dark)] hover:bg-white/5"
        >
          Cancel
        </button>
      </div>
      {formError ? <p className="text-xs text-rose-200/90">{formError}</p> : null}
    </form>
  );
}

function TicketDialog({
  open,
  onClose,
  reg,
}: {
  open: boolean;
  onClose: () => void;
  reg: RegRow | null;
}) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  const payload = useMemo(() => {
    if (!reg?.marketing_events || !reg.ticket_code) return '';
    return JSON.stringify({ eventId: reg.event_id, ticket: reg.ticket_code, title: reg.marketing_events.title });
  }, [reg]);

  useEffect(() => {
    if (!open || !payload) {
      setQrDataUrl(null);
      return;
    }
    let cancelled = false;
    void QRCode.toDataURL(payload, { margin: 1, width: 220 }).then((url: string) => {
      if (!cancelled) setQrDataUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [open, payload]);

  function downloadHtml() {
    if (!reg?.marketing_events) return;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Ticket</title></head><body style="font-family:system-ui;padding:2rem;">
<h1>Event ticket</h1>
<p><strong>Event:</strong> ${escapeHtml(reg.marketing_events.title)}</p>
<p><strong>Guest:</strong> ${escapeHtml(reg.full_name)}</p>
<p><strong>Email:</strong> ${escapeHtml(reg.email)}</p>
<p><strong>Ticket #:</strong> ${escapeHtml(reg.ticket_code ?? '—')}</p>
<p><strong>When:</strong> ${escapeHtml(new Date(reg.marketing_events.starts_at).toLocaleString())}</p>
<p><strong>Venue:</strong> ${escapeHtml(reg.marketing_events.venue)}</p>
</body></html>`;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `ticket-${(reg.ticket_code ?? 'event').replace(/[^a-z0-9-]/gi, '_')}.html`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  function escapeHtml(s: string) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  if (!open || !reg?.marketing_events) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-end justify-center sm:items-center" role="dialog" aria-modal aria-labelledby="ticket-title">
      <button type="button" className="absolute inset-0 bg-black/70" aria-label="Close" onClick={onClose} />
      <div className={`relative z-10 m-4 max-h-[90vh] w-full max-w-md overflow-y-auto p-6 ${SITE_GLASS_CARD}`}>
        <h2 id="ticket-title" className="font-display text-lg font-bold text-[var(--home-text-on-dark)]">
          Your ticket
        </h2>
        <p className="mt-2 text-sm text-[var(--home-muted-on-dark)]">{reg.marketing_events.title}</p>
        {reg.ticket_code ? (
          <>
            <p className="mt-4 font-mono text-sm text-[var(--home-text-on-dark)]">#{reg.ticket_code}</p>
            <p className="mt-1 text-xs text-[var(--home-muted-on-dark)]">
              {reg.full_name} · {reg.email}
            </p>
            {qrDataUrl ? (
              <div className="mt-4 flex justify-center rounded-xl bg-white p-3">
                <img src={qrDataUrl} alt="Ticket QR code" width={220} height={220} />
              </div>
            ) : (
              <p className="mt-4 text-sm text-[var(--home-muted-on-dark)]">Generating QR…</p>
            )}
            <p className="mt-3 text-xs text-[var(--home-muted-on-dark)]">
              Scan at check-in. Wallet pass distribution requires Apple Developer Program setup — use download for now.
            </p>
            <button
              type="button"
              disabled
              className="mt-3 w-full cursor-not-allowed rounded-xl border border-white/15 bg-white/5 py-2.5 text-sm text-[var(--home-muted-on-dark)]"
              title="Requires Apple Wallet / pkpass provisioning"
            >
              Add to Apple Wallet (coming soon)
            </button>
            <button
              type="button"
              onClick={downloadHtml}
              className="mt-2 w-full rounded-xl bg-[var(--home-miami-blue)] py-2.5 text-sm font-semibold text-[var(--home-zone-void)] hover:brightness-105"
            >
              Download ticket (HTML)
            </button>
          </>
        ) : (
          <p className="mt-4 text-sm text-[var(--home-muted-on-dark)]">
            This is a virtual event — join details were emailed to you.
            {reg.marketing_events.event_url ? (
              <a href={reg.marketing_events.event_url} className="mt-2 block font-medium text-[var(--home-miami-blue)] underline">
                Open event link
              </a>
            ) : null}
          </p>
        )}
        <button
          type="button"
          onClick={onClose}
          className="mt-4 w-full rounded-xl border border-white/15 py-2.5 text-sm text-[var(--home-text-on-dark)] hover:bg-white/5"
        >
          Close
        </button>
      </div>
    </div>
  );
}

export function InvestorEvents() {
  const { session, profile } = useAuth();
  const token = session?.access_token ?? '';

  const [upcoming, setUpcoming] = useState<MarketingEvent[]>([]);
  const [regs, setRegs] = useState<RegRow[]>([]);
  const [ticketReg, setTicketReg] = useState<RegRow | null>(null);

  const refreshRegs = useCallback(async () => {
    if (!token) return;
    const r = await apiFetch<{ registrations: RegRow[] }>('/api/investor/event-registrations', { token });
    setRegs(r.registrations);
  }, [token]);

  useEffect(() => {
    let c = false;
    void (async () => {
      try {
        const res = await fetch(apiUrl('/api/public/events'));
        if (!res.ok) return;
        const data = (await res.json()) as { upcoming?: MarketingEvent[] };
        if (!c) setUpcoming(data.upcoming ?? []);
      } catch {
        if (!c) setUpcoming([]);
      }
    })();
    return () => {
      c = true;
    };
  }, []);

  useEffect(() => {
    void refreshRegs();
  }, [refreshRegs]);

  useEffect(() => {
    if (profile?.email) {
      /* prefill not needed for list view */
    }
  }, [profile?.email]);

  return (
    <div className="space-y-10">
      <header>
        <p className="font-display text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-[var(--home-miami-blue)]">Investor portal</p>
        <h1 className="mt-2 font-display text-2xl font-bold text-[var(--home-text-on-dark)] md:text-3xl">Events</h1>
        <p className="mt-2 max-w-2xl text-sm text-[var(--home-muted-on-dark)]">
          Upcoming programs you can join — and registrations tied to your account.
        </p>
      </header>

      <div className="grid gap-8 lg:grid-cols-2">
        <section className={`${SITE_GLASS_CARD} p-5`}>
          <h2 className="font-display text-sm font-bold uppercase tracking-[0.14em] text-[var(--home-text-on-dark)]">Upcoming</h2>
          <ul className="mt-4 space-y-4">
            {upcoming.length === 0 ? (
              <li className="text-sm text-[var(--home-muted-on-dark)]">No scheduled events — check back soon.</li>
            ) : (
              upcoming.map((ev) => {
                const img = ev.image_path
                  ? supabase.storage.from('event-assets').getPublicUrl(ev.image_path).data.publicUrl
                  : '';
                return (
                  <li key={ev.id} className="overflow-hidden rounded-xl border border-white/10 bg-black/20">
                    {img ? (
                      <img src={img} alt="" className="h-48 w-full object-cover sm:h-52" loading="lazy" />
                    ) : (
                      <div className="h-48 bg-gradient-to-br from-neutral-800 to-neutral-950 sm:h-52" />
                    )}
                    <div className="p-4">
                      <span
                        className={`inline-block rounded-md px-2 py-0.5 font-display text-[0.58rem] font-bold uppercase tracking-[0.1em] ring-1 ${
                          ev.is_online
                            ? 'bg-sky-500/15 text-sky-100 ring-sky-400/30'
                            : 'bg-white/10 text-white/90 ring-white/12'
                        }`}
                      >
                        {ev.is_online ? 'Online' : 'In person'}
                      </span>
                      <h3 className="mt-2 font-display text-sm font-bold uppercase tracking-wide text-[var(--home-text-on-dark)]">{ev.title}</h3>
                      {ev.description ? (
                        <p className="mt-2 text-xs leading-relaxed text-[var(--home-muted-on-dark)]">{ev.description}</p>
                      ) : null}
                      <p className="mt-2 flex items-start gap-2 text-xs text-[var(--home-muted-on-dark)]">
                        <IconPin className="mt-0.5 shrink-0 text-[var(--home-miami-blue)]" />
                        {ev.venue}
                      </p>
                      <p className="mt-1 text-xs text-[var(--home-muted-on-dark)]">
                        {new Date(ev.starts_at).toLocaleString(undefined, { timeZone: ev.timezone })} · {ev.time_label ?? ''}
                      </p>
                      {ev.is_online && ev.event_url ? (
                        <a
                          href={ev.event_url}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-2 inline-block text-xs font-medium text-[var(--home-miami-blue)] underline underline-offset-2"
                        >
                          Join link
                        </a>
                      ) : null}
                      <EventRsvpForm eventId={ev.id} token={token} onSuccess={() => void refreshRegs()} />
                    </div>
                  </li>
                );
              })
            )}
          </ul>
        </section>

        <section className={`${SITE_GLASS_CARD} p-5`}>
          <h2 className="font-display text-sm font-bold uppercase tracking-[0.14em] text-[var(--home-text-on-dark)]">Your registrations</h2>
          <ul className="mt-4 space-y-4">
            {regs.length === 0 ? (
              <li className="text-sm text-[var(--home-muted-on-dark)]">No RSVPs yet — reserve a seat from the left column.</li>
            ) : (
              regs.map((r) => {
                const ev = r.marketing_events;
                if (!ev) return null;
                return (
                  <li key={r.id} className="rounded-xl border border-white/10 bg-black/20 p-4">
                    <p className="font-display text-sm font-bold text-[var(--home-text-on-dark)]">{ev.title}</p>
                    <p className="mt-1 text-xs text-[var(--home-muted-on-dark)]">
                      {new Date(ev.starts_at).toLocaleString(undefined, { timeZone: ev.timezone })}
                    </p>
                    {r.ticket_code ? (
                      <p className="mt-2 font-mono text-xs text-[var(--home-miami-blue)]">Ticket {r.ticket_code}</p>
                    ) : null}
                    {r.attended_at ? (
                      <p className="mt-2 text-xs font-medium text-emerald-300">Checked in {new Date(r.attended_at).toLocaleString()}</p>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => setTicketReg(r)}
                      className="mt-3 w-full rounded-lg bg-[var(--header-shell-bg)] py-2 text-center font-display text-[0.62rem] font-bold uppercase tracking-[0.12em] text-white hover:brightness-110"
                    >
                      Ticket &amp; QR
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </section>
      </div>

      <TicketDialog open={Boolean(ticketReg)} onClose={() => setTicketReg(null)} reg={ticketReg} />
    </div>
  );
}
